import { CheckoutSessionInitializationError } from "@circle-react/components/Paywalls/Checkout/sessionOwnership";
import { ErrorTemplate } from "@circle-react/components/shared/ErrorPages/ErrorTemplate";
import { Button } from "@circleco/compass/components/Button";
import { Typography } from "@circleco/compass/components/Typography";
import type { MutableRefObject } from "react";
import { useCallback, useState } from "react";

import { t } from "@/i18n-js/instance";

interface CheckoutSessionErrorPageProps {
  createError: Error | null;
  hasRetriedRef: MutableRefObject<boolean>;
  refreshSession: (rejectedSessionId?: number) => Promise<void>;
}

export const CheckoutSessionErrorPage = ({
  createError,
  hasRetriedRef,
  refreshSession,
}: CheckoutSessionErrorPageProps) => {
  const initializationError =
    createError instanceof CheckoutSessionInitializationError
      ? createError
      : null;
  const [hasRetried, setHasRetried] = useState(hasRetriedRef.current);
  const retry = useCallback(() => {
    const rejectedSessionId = initializationError?.rejectedSessionId;
    if (
      hasRetriedRef.current ||
      !initializationError?.canRetryWithFreshSession ||
      rejectedSessionId === undefined
    ) {
      return;
    }

    hasRetriedRef.current = true;
    setHasRetried(true);
    void refreshSession(rejectedSessionId).catch(() => undefined);
  }, [hasRetriedRef, initializationError, refreshSession]);

  if (initializationError?.canRetryWithFreshSession) {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-primary px-6 text-center">
        <Typography variant="heading-md" color="primary">
          {t("paywall_checkout.error_boundary.title")}
        </Typography>
        {!hasRetried && (
          <Button onClick={retry}>{t("error_page.try_again")}</Button>
        )}
      </main>
    );
  }

  return (
    <ErrorTemplate
      errorCode={500}
      title={t("paywall_checkout.error_boundary.title")}
    />
  );
};
